import { useEffect, useState } from "react";
import { Instagram, Loader2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { dealerIgStatus, dealerIgDisconnect, dealerIgConnectUrl, dealerIgTesterRequest } from "@/lib/api";

// Dealer-owned Instagram Business account → lets the dealer post review cards / Stories to THEIR feed.
export default function DealerInstagramSettings({ userRecordId }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  function load() {
    setLoading(true); setFailed(false);
    dealerIgStatus(userRecordId).then((s) => { setSt(s); setFailed(false); }).catch(() => { setSt(null); setFailed(true); }).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [userRecordId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("ig_connected")) toast.success(`Instagram @${p.get("ig_connected")} connected — you can now post reviews to your feed and Stories`, { duration: 8000 });
    if (p.get("ig_error")) toast.error(`Instagram connection failed: ${p.get("ig_error")}`, { duration: 9000 });
    if (p.get("ig_connected") || p.get("ig_error")) {
      p.delete("ig_connected"); p.delete("ig_error");
      window.history.replaceState({}, "", `${window.location.pathname}${p.toString() ? `?${p}` : ""}`);
    }
  }, []);

  async function disconnect() {
    if (!window.confirm(`Disconnect Instagram @${st?.username || ""}? You'll need to connect again before posting reviews.`)) return;
    setBusy(true);
    try {
      await dealerIgDisconnect(userRecordId);
      setSt((s) => ({ ...(s || {}), connected: false, username: "" }));
      toast.success("Instagram disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    } finally { setBusy(false); }
  }

  async function askTester() {
    const handle = window.prompt("Your Instagram username (the account you'll connect):", st?.instagram || "");
    if (handle == null) return;
    if (!handle.trim()) { toast.error("Enter your Instagram username"); return; }
    setBusy(true);
    try {
      const r = await dealerIgTesterRequest(userRecordId, handle.trim());
      setSt((s) => ({ ...(s || {}), tester_requested: true, instagram: r.handle }));
      toast.success(`Request sent — OreVital will add @${r.handle} as a tester and let you know`, { duration: 7000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the request");
    } finally { setBusy(false); }
  }

  if (loading) return <Loader2 className="animate-spin text-zinc-400" size={16} />;
  const connected = !!st?.connected;
  const connectHref = dealerIgConnectUrl(userRecordId, `${window.location.pathname}?tab=settings`);

  return (
    <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="dealer-ig-settings">
      <div className="flex items-center gap-2 mb-1">
        <Instagram size={15} className="text-pink-600" />
        <span className="text-sm font-semibold text-zinc-800">Your Instagram — post reviews to your feed & Stories</span>
        {connected && (
          <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid="dealer-ig-connected-badge">
            Connected · @{st.username}{st.days_left != null ? ` · ${st.days_left}d` : ""}
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        Connect your Instagram <b>Business or Creator</b> account. Every approved product review then gets a
        "Post to @{st?.username || "your-account"}" button with a branded card whose footer links to your shop page.
      </p>
      {failed ? (
        <p className="text-xs text-rose-700 bg-rose-50 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="dealer-ig-load-failed">
          Couldn't reach the server to check your Instagram connection.
          <button type="button" className="font-semibold underline" onClick={load} data-testid="dealer-ig-retry">Retry</button>
        </p>
      ) : !st?.login_configured ? (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="dealer-ig-not-configured">
          Instagram connect isn't switched on yet — OreVital admin needs to add the Instagram App ID & Secret.
        </p>
      ) : st?.eligible === false ? (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="dealer-ig-not-eligible">
          Instagram posting unlocks once your dealer account is approved by OreVital.
        </p>
      ) : connected ? (
        <div className="flex flex-wrap items-center gap-2">
          <a href={connectHref} className="inline-flex items-center h-8 px-3 rounded-lg text-xs font-medium text-violet-700 hover:bg-violet-50" data-testid="dealer-ig-reconnect">
            <RefreshCw size={13} className="mr-1" /> Reconnect
          </a>
          <Button size="sm" variant="ghost" disabled={busy} className="h-8 rounded-lg text-rose-600 hover:bg-rose-50" onClick={disconnect} data-testid="dealer-ig-disconnect">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <><X size={13} className="mr-1" /> Disconnect</>}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <a href={connectHref} className="inline-flex items-center h-9 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-pink-600 to-fuchsia-600 hover:opacity-90" data-testid="dealer-ig-connect">
              <Instagram size={15} className="mr-1.5" /> Connect Instagram
            </a>
            {st?.in_review && !st?.is_admin && (
              <Button size="sm" variant="outline" disabled={busy || st?.tester_requested} className="h-9 rounded-lg text-xs" onClick={askTester} data-testid="dealer-ig-ask-tester">
                {busy ? <Loader2 className="animate-spin" size={13} /> : st?.tester_requested ? "Early access requested ✓" : "Ask for early access"}
              </Button>
            )}
          </div>
          {st?.in_review && !st?.is_admin && (
            <p className="text-[11px] text-zinc-500" data-testid="dealer-ig-review-note">
              Meta is still reviewing OreVital's Instagram app. If Instagram shows "Insufficient developer role", tap <b>Ask for early access</b> — we'll add your account as a tester (you'll get an invite in Instagram → Settings → Apps and websites → Tester invites), then connect again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
